import React, { useEffect, useState } from 'react';
import { colocService, setSelectedColoc } from '../services/coloc.service';
import { roomsService } from '../services/rooms.service';
import api from '../services/api.service';
import PhotoManager from '../components/PhotoManager';
import { onboardingService } from '../services/onboarding.service';
import AddressPicker, { type AdressePostale } from '../components/AddressPicker';
import BathroomSharing from '../components/BathroomSharing';
import FieldPreview from '../components/FieldPreview';
import { APERCUS_CHAMBRE } from './RoomsPage';
import { capabilitiesService } from '../services/capabilities.service';
import type { Coloc } from '../types/coloc';
import type { RoomDetail } from '../types/room';
import './OnboardingPage.css';
import { DEV, fichierPhotoDeTest } from '../utils/devFixtures';

function slugify(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[-\s]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Préremplissage de confort, DÉVELOPPEMENT UNIQUEMENT.
 *
 * `NODE_ENV` est figé à la compilation : dans un build de production la
 * constante est `false` et les valeurs sont remplacées par du vide — le
 * formulaire se comporte alors exactement comme avant.
 *
 * Les champs du wizard sont CONTRÔLÉS : on initialise donc l'état React, et
 * non la valeur du DOM. Écrire dans le DOM donnerait des champs visuellement
 * remplis mais un état vide, et l'envoi partirait sans données.
 */
// `DEV` et le générateur d'image vivent dans utils/devFixtures : la carte de
// publication s'en sert aussi pour son remplissage automatique.
const dev = (valeur: string): string => (DEV ? valeur : '');

type Phase = 'welcome' | 'coloc' | 'colocPhoto' | 'room' | 'photo' | 'done';

const PROGRESS: Record<Phase, number> = {
  welcome: 0, coloc: 1, colocPhoto: 1, room: 2, photo: 2, done: 3,
};

const OnboardingPage: React.FC = () => {
  const [phase, setPhase] = useState<Phase>('welcome');
  const [coloc, setColoc] = useState<Coloc | null>(null);
  const [room, setRoom] = useState<RoomDetail | null>(null);
  const [loading, setLoading] = useState(true);
  // null = inconnu (appel en échec) : on n'affiche alors aucun rappel plutôt
  // qu'un message éventuellement faux.
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);

  useEffect(() => {
    capabilitiesService
      .getCapabilities()
      .then((c) => setEmailVerified(c.emailVerified))
      .catch(() => setEmailVerified(null));
  }, []);

  const handleSkip = () => {
    onboardingService.skip();
    // Rechargement plutôt que navigation React : la garde d'onboarding lit son
    // état au montage, et un simple changement de route la laisserait sur sa
    // décision précédente — donc renverrait ici.
    window.location.href = '/dashboard';
  };

  // Reprise : on déduit où en est l'owner (cf. onboarding.service).
  useEffect(() => {
    onboardingService
      .getState()
      .then((s) => {
        if (s.done) {
          window.location.href = '/dashboard';
          return;
        }
        setColoc(s.coloc);
        setRoom(s.room);
        // welcome seulement si rien n'est commencé
        setPhase(s.step === 'coloc' ? 'welcome' : s.step);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="ob-screen"><p>Chargement…</p></div>;
  }

  return (
    <div className="ob-wrap">
      <header className="ob-header">
        <span className="ob-logo">COLOC.APP</span>
        <ObProgress current={PROGRESS[phase]} />
        {/* Sortie de secours, disponible à toutes les étapes : sans elle, le
            wizard est un cul-de-sac — la garde d'onboarding ramène ici toute
            tentative d'aller ailleurs. */}
        <button type="button" className="ob-skip" onClick={handleSkip}>
          Passer
        </button>
      </header>

      <main className="ob-main">
        {phase === 'welcome' && (
          <Welcome onNext={() => setPhase('coloc')} emailVerified={emailVerified} />
        )}
        {phase === 'coloc' && (
          <ColocStep
            onCreated={(c) => {
              setColoc(c);
              setSelectedColoc(c);
              setPhase('colocPhoto');
            }}
          />
        )}
        {phase === 'colocPhoto' && coloc && (
          <PhotoStep
            entityType="coloc"
            entityId={coloc.id}
            titre="Étape 1 — Les photos de la colocation"
            sousTitre="Parties communes, façade, extérieurs : c'est la première chose que voient les candidats, et une photo au moins est nécessaire pour mettre le site en ligne."
            onDone={() => setPhase('room')}
          />
        )}
        {phase === 'room' && coloc && (
          <RoomStep
            colocId={coloc.id}
            onCreated={(r) => {
              setRoom(r);
              setPhase('photo');
            }}
          />
        )}
        {phase === 'photo' && room && (
          <PhotoStep
            entityType="room"
            entityId={room.id}
            titre="Étape 2 — La photo de la chambre"
            sousTitre="Au moins une photo est nécessaire pour que votre chambre soit attractive."
            onDone={() => setPhase('done')}
          />
        )}
        {phase === 'done' && coloc && <DoneStep coloc={coloc} />}
      </main>
    </div>
  );
};

const ObProgress: React.FC<{ current: number }> = ({ current }) => (
  <div className="ob-progress" aria-label={`Étape ${current} sur 3`}>
    {[1, 2, 3].map((n) => (
      <span key={n} className={`ob-dot${n <= current ? ' filled' : ''}`} />
    ))}
  </div>
);

const Welcome: React.FC<{ onNext: () => void; emailVerified: boolean | null }> = ({
  onNext,
  emailVerified,
}) => (
  <div className="ob-card ob-center">
    <h1>Votre colocation en ligne en quelques minutes.</h1>
    <p className="ob-sub">
      Vous allez créer votre colocation, ajouter une première chambre avec une photo,
      et obtenir le lien de votre site public — prêt à diffuser.
    </p>
    {emailVerified === false && (
      // Rappel non bloquant : la vérification n'est exigée qu'au moment de la
      // mise en ligne, mais c'est ici qu'elle coûte le moins cher à faire.
      <p className="ob-mail-note">
        Un email vient de vous être envoyé. Cliquez sur le lien qu&apos;il contient
        pour confirmer votre adresse — elle devra l&apos;être avant la mise en ligne
        de votre site.
      </p>
    )}
    <button className="ob-btn" onClick={onNext}>Commencer</button>
  </div>
);

const ColocStep: React.FC<{ onCreated: (c: Coloc) => void }> = ({ onCreated }) => {
  const [name, setName] = useState(dev('Coloc Vauban Lille'));
  // Suffixe aléatoire : le slug doit être unique, sinon le second passage
  // dans le wizard bute sur « déjà pris ».
  const [slug, setSlug] = useState(() =>
    dev(`coloc-vauban-lille-${Math.random().toString(36).slice(2, 6)}`),
  );
  // `true` en développement : sans ça, la moindre frappe dans le nom
  // réécrirait le slug depuis le nom et effacerait le suffixe unique.
  const [slugEdited, setSlugEdited] = useState(DEV);
  // Un seul objet : c'est le contrat d'AddressPicker, partage avec la fiche
  // Details. Le decouper ici obligerait a le recomposer a chaque rendu.
  const [adresse, setAdresse] = useState<AdressePostale>({
    address: dev('12 rue de la Paix'),
    zipCode: dev('59000'),
    city: dev('Lille'),
    countryCode: 'FR',
    location: null,
  });
  // Les quatre colonnes de texte de `colocs` sont NOT NULL. Elles étaient
  // remplies avec "" sans qu'on demande rien : le wizard les pose désormais,
  // comme les nombres de l'étape 2.
  const [introTxt, setIntroTxt] = useState(dev(
    'Colocation rénovée de 5 chambres à deux pas du centre, pensée pour des ' +
    'étudiants et jeunes actifs.'));
  const [featuresTxt, setFeaturesTxt] = useState(dev(
    'Maison de ville sur trois niveaux : grand salon, cuisine entièrement ' +
    'équipée, buanderie et cour intérieure.'));
  // Des CASES, pas un texte : le bloc « Services » du site public s'affiche
  // dès qu'une case est cochée, donc `amenities_txt` est un vrai facultatif —
  // le complément libre reste sur la fiche Détails.
  const [equipements, setEquipements] = useState<Record<string, boolean>>(
    DEV ? { isWifi: true, isWashingMachine: true, isDishwasher: true } : {},
  );
  const [locationTxt, setLocationTxt] = useState(dev(
    'À 5 minutes du métro, commerces et supermarché au pied de la maison.'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slugCheck, setSlugCheck] = useState<{ available: boolean; suggestions: string[] } | null>(null);

  const effectiveSlug = slug;

  // Garde-fou slug : vérifie la dispo (debounced) via l'endpoint backend.
  useEffect(() => {
    if (!effectiveSlug) { setSlugCheck(null); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/colocs/slug-available', { params: { slug: effectiveSlug } });
        setSlugCheck({ available: res.data.available, suggestions: res.data.suggestions });
      } catch {
        setSlugCheck(null);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [effectiveSlug]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (slugCheck && !slugCheck.available) {
      setError('Ce slug est déjà pris, choisissez-en un autre.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const c = await colocService.createColoc({
        name,
        address: adresse.address,
        zipCode: adresse.zipCode,
        city: adresse.city,
        countryCode: adresse.countryCode,
        slug: effectiveSlug || undefined,
        location: adresse.location ?? undefined,
        introTxt, featuresTxt, locationTxt,
        ...equipements,
      });
      onCreated(c);
    } catch (err: unknown) {
      const detail =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response: { data?: { detail?: string } } }).response.data?.detail
          : null;
      setError(detail || 'Erreur lors de la création.');
      setSaving(false);
    }
  };

  return (
    <form className="ob-card" onSubmit={submit}>
      <h2>Étape 1 — Votre colocation</h2>
      {error && <div className="ob-error">{error}</div>}

      <label>Nom de la colocation
        <input value={name} required placeholder="Ex : Coloc Vauban Lille"
          onChange={(e) => { setName(e.target.value); if (!slugEdited) setSlug(slugify(e.target.value)); }} />
      </label>

      {/* « URL » et non « adresse » : le champ d'adresse postale est juste
          en dessous, et les deux se confondaient à la lecture. */}
      <label>URL de votre site (slug)
        <input value={slug} required placeholder="coloc-vauban-lille"
          onChange={(e) => { setSlugEdited(true); setSlug(slugify(e.target.value)); }} />
      </label>
      {effectiveSlug && (
        <p className="ob-slug-preview">
          Votre site : <strong>{effectiveSlug}.coloc.app</strong>
          {slugCheck && (slugCheck.available
            ? <span className="ob-ok"> ✓ disponible</span>
            : <span className="ob-ko"> ✗ déjà pris</span>)}
        </p>
      )}
      {slugCheck && !slugCheck.available && slugCheck.suggestions.length > 0 && (
        <p className="ob-suggestions">
          Suggestions :{' '}
          {slugCheck.suggestions.map((s) => (
            <button type="button" key={s} className="ob-chip"
              onClick={() => { setSlugEdited(true); setSlug(s); }}>{s}</button>
          ))}
        </p>
      )}
      <p className="ob-note">⚠️ L&apos;URL du site est définitive : elle ne pourra plus être modifiée.</p>

      <AddressPicker value={adresse} onChange={setAdresse} variante="wizard" />

      <label>
        <span className="ob-label">Présentation de la colocation
          <FieldPreview apercu="presentation.png" titre="Premier paragraphe de la page"
            description="Deux ou trois phrases qui situent la colocation : le quartier, ce qui la distingue, à qui elle s'adresse."
            exemple="Colocation récente de 7 chambres située à Lille-Fives, au pied du métro Ligne 1. Accès direct Universités et Centre-Ville." />
        </span>
        <textarea value={introTxt} required rows={2}
          onChange={(e) => setIntroTxt(e.target.value)} /></label>

      <label>
        <span className="ob-label">Description du logement
          <FieldPreview apercu="caracteristiques.png" titre="Corps de « À propos de cette colocation »"
            description="Le texte long qui décrit le logement : agencement, ambiance, parties communes."
            exemple="Colocation rénovée en 2021, idéalement placée au pied de la ligne 1 du métro. Composée de 7 chambres, elle propose des espaces communs spacieux avec deux cuisines entièrement équipées, un salon / salle à manger et une buanderie." />
        </span>
        <textarea value={featuresTxt} required rows={3}
          onChange={(e) => setFeaturesTxt(e.target.value)} /></label>

      <div className="ob-equipements">
        <span className="ob-label">Équipements et services
          <FieldPreview apercu="equipements.png" titre="Bloc « Services »"
            description="Chaque case cochée devient une ligne du bloc. Un texte libre pourra compléter depuis la fiche Détails."
            exemple="Des services complémentaires de conciergerie et de nettoyage des parties communes sont inclus dans les charges." />
        </span>
        <div className="ob-cases">
          <label key="isWifi"><input type="checkbox" checked={!!equipements.isWifi}
            onChange={(e) => setEquipements((x) => ({ ...x, isWifi: e.target.checked }))} />Wifi</label>
          <label key="isWashingMachine"><input type="checkbox" checked={!!equipements.isWashingMachine}
            onChange={(e) => setEquipements((x) => ({ ...x, isWashingMachine: e.target.checked }))} />Lave-linge</label>
          <label key="isDryer"><input type="checkbox" checked={!!equipements.isDryer}
            onChange={(e) => setEquipements((x) => ({ ...x, isDryer: e.target.checked }))} />Sèche-linge</label>
          <label key="isDishwasher"><input type="checkbox" checked={!!equipements.isDishwasher}
            onChange={(e) => setEquipements((x) => ({ ...x, isDishwasher: e.target.checked }))} />Lave-vaisselle</label>
          <label key="isCleaningServices"><input type="checkbox" checked={!!equipements.isCleaningServices}
            onChange={(e) => setEquipements((x) => ({ ...x, isCleaningServices: e.target.checked }))} />Ménage des communs</label>
          <label key="isConcierge"><input type="checkbox" checked={!!equipements.isConcierge}
            onChange={(e) => setEquipements((x) => ({ ...x, isConcierge: e.target.checked }))} />Conciergerie</label>
          <label key="isGarden"><input type="checkbox" checked={!!equipements.isGarden}
            onChange={(e) => setEquipements((x) => ({ ...x, isGarden: e.target.checked }))} />Jardin</label>
          <label key="isBalcony"><input type="checkbox" checked={!!equipements.isBalcony}
            onChange={(e) => setEquipements((x) => ({ ...x, isBalcony: e.target.checked }))} />Balcon ou terrasse</label>
          <label key="isStorage"><input type="checkbox" checked={!!equipements.isStorage}
            onChange={(e) => setEquipements((x) => ({ ...x, isStorage: e.target.checked }))} />Cave ou rangement</label>
          <label key="isBikeStorage"><input type="checkbox" checked={!!equipements.isBikeStorage}
            onChange={(e) => setEquipements((x) => ({ ...x, isBikeStorage: e.target.checked }))} />Local à vélos</label>
          <label key="isAirConditioning"><input type="checkbox" checked={!!equipements.isAirConditioning}
            onChange={(e) => setEquipements((x) => ({ ...x, isAirConditioning: e.target.checked }))} />Climatisation</label>
          <label key="isElevator"><input type="checkbox" checked={!!equipements.isElevator}
            onChange={(e) => setEquipements((x) => ({ ...x, isElevator: e.target.checked }))} />Ascenseur</label>
          <label key="isPetAllowed"><input type="checkbox" checked={!!equipements.isPetAllowed}
            onChange={(e) => setEquipements((x) => ({ ...x, isPetAllowed: e.target.checked }))} />Animaux autorisés</label>
        </div>
      </div>

      <label>
        <span className="ob-label">Le quartier
          <FieldPreview apercu="quartier.png" titre="Texte du bloc « À proximité »"
            description="Transports, commerces, campus : ce qui rend l'emplacement pratique au quotidien."
            exemple="Cette colocation est idéalement située. À 100 m du métro Ligne 1, celui-ci vous amène directement vers la Cité scientifique et l'université Pont de Bois." />
        </span>
        <textarea value={locationTxt} required rows={2}
          onChange={(e) => setLocationTxt(e.target.value)} /></label>

      <button className="ob-btn" type="submit" disabled={saving || (!!slugCheck && !slugCheck.available)}>
        {saving ? 'Création…' : 'Créer ma colocation'}
      </button>
    </form>
  );
};

const RoomStep: React.FC<{ colocId: string; onCreated: (r: RoomDetail) => void }> = ({ colocId, onCreated }) => {
  const [name, setName] = useState(dev('Chambre 1'));
  // La surface manquait à l'appel. Faute d'être demandée, elle valait 0 en base
  // et l'annonce publiée affichait « Chambre de 0 m² » — jusque dans la
  // description reprise par Google et les réseaux quand un candidat la partage.
  const [surface, setSurface] = useState(dev('12'));
  const [priceNIC, setPriceNIC] = useState(dev('450'));
  const [charges, setCharges] = useState(dev('50'));
  const [introTxt, setIntroTxt] = useState(
    dev(
      'Chambre lumineuse de 12 m² au premier étage, avec lit double, bureau ' +
        'et grand placard. Fenêtre côté cour, très calme.',
    ),
  );
  // Ces quatre champs sont NOT NULL en base : le wizard les DEMANDE au lieu de
  // les inventer. Une valeur choisie par le produit se retrouvait publiée sur le
  // site sans que le propriétaire l'ait jamais vue — « SdB partagée » et un
  // dépôt de garantie à 0 pour toute chambre créée par le wizard.
  const [bathroomShared, setBathroomShared] = useState<number | null>(DEV ? 1 : null);
  const [deposit, setDeposit] = useState(dev('450'));
  const [agencyExpenses, setAgencyExpenses] = useState(dev('0'));
  const [tantiemes, setTantiemes] = useState(dev('100'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (bathroomShared === null) {
      setError('Indiquez si la salle de bain est privative ou partagée.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const r = await roomsService.create(colocId, {
        name,
        introTxt,
        surface: Number(surface) || 0,
        priceNIC: Number(priceNIC) || 0,
        charges: Number(charges) || 0,
        bathroomSharedWith: bathroomShared,
        agencyExpenses: Number(agencyExpenses) || 0,
        depositAmount: Number(deposit) || 0,
        tantiemes: Number(tantiemes) || 0,
      });
      onCreated(r);
    } catch (err: unknown) {
      const detail =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response: { data?: { detail?: string } } }).response.data?.detail
          : null;
      setError(detail || 'Erreur lors de la création de la chambre.');
      setSaving(false);
    }
  };

  return (
    <form className="ob-card" onSubmit={submit}>
      <h2>Étape 2 — Votre première chambre</h2>
      <p className="ob-sub">Vous pourrez en ajouter d&apos;autres et affiner les détails ensuite.</p>
      {error && <div className="ob-error">{error}</div>}
      <label><span className="ob-label">Nom de la chambre<FieldPreview {...APERCUS_CHAMBRE.nom} /></span><input value={name} required onChange={(e) => setName(e.target.value)} placeholder="Ex : Chambre 1" /></label>
      <div className="ob-row">
        <label>Surface (m²)
          <input type="number" min="1" step="0.5" value={surface} required
            onChange={(e) => setSurface(e.target.value)} placeholder="12" /></label>
        <label><span className="ob-label">Loyer hors charges (€)<FieldPreview {...APERCUS_CHAMBRE.loyer} /></span><input type="number" min="0" value={priceNIC} required onChange={(e) => setPriceNIC(e.target.value)} placeholder="450" /></label>
        <label>Charges (€)<input type="number" min="0" value={charges} required onChange={(e) => setCharges(e.target.value)} placeholder="50" /></label>
      </div>
      <div className="ob-sdb-ligne">
        <BathroomSharing id="ob-sdb" value={bathroomShared} onChange={setBathroomShared} />
        <FieldPreview {...APERCUS_CHAMBRE.sdb} />
      </div>
      <div className="ob-row">
        <label>Dépôt de garantie (€)
          <input type="number" min="0" value={deposit} required
            onChange={(e) => setDeposit(e.target.value)} placeholder="450" /></label>
        <label>Frais d&apos;agence (€)
          <input type="number" min="0" value={agencyExpenses} required
            onChange={(e) => setAgencyExpenses(e.target.value)} placeholder="0" /></label>
        <label>Tantièmes
          <input type="number" min="0" value={tantiemes} required
            onChange={(e) => setTantiemes(e.target.value)} placeholder="100" /></label>
      </div>
      <label><span className="ob-label">Description<FieldPreview {...APERCUS_CHAMBRE.description} /></span><textarea value={introTxt} required rows={3} onChange={(e) => setIntroTxt(e.target.value)} placeholder="Chambre lumineuse de 12 m²…" /></label>
      <button className="ob-btn" type="submit" disabled={saving}>{saving ? 'Création…' : 'Ajouter la chambre'}</button>
    </form>
  );
};

/** Fabrique une image JPEG à la volée : l'étape photo attend un fichier, qui
 *  ne peut pas se préremplir comme un champ de saisie. Développement seulement. */
/**
 * Étape photo, commune à la colocation et à la chambre — les deux sont des
 * bloquants de publication (cf. usecase/publication_readiness.py), et le
 * parcours ne diffère que par les libellés.
 */
const PhotoStep: React.FC<{
  entityType: 'coloc' | 'room';
  entityId: string;
  titre: string;
  sousTitre: string;
  onDone: () => void;
}> = ({ entityType, entityId, titre, sousTitre, onDone }) => {
  const [hasPhoto, setHasPhoto] = useState(false);
  const [envoiTest, setEnvoiTest] = useState(false);
  // Change après un envoi pour forcer PhotoManager à recharger sa liste :
  // il ne surveille pas les ajouts faits en dehors de lui.
  const [rafraichir, setRafraichir] = useState(0);

  const ajouterPhotoDeTest = async () => {
    setEnvoiTest(true);
    try {
      const { photosService } = await import('../services/photos.service');
      await photosService.upload(entityType, entityId, await fichierPhotoDeTest());
      setRafraichir((n) => n + 1);
    } finally {
      setEnvoiTest(false);
    }
  };

  // On surveille l'ajout d'au moins une photo (la photo est obligatoire).
  useEffect(() => {
    setHasPhoto(false); // l'étape suivante repart à zéro, pas sur l'état de la précédente
    const id = setInterval(async () => {
      try {
        const { photosService } = await import('../services/photos.service');
        const photos = await photosService.list(entityType, entityId);
        setHasPhoto(photos.length > 0);
      } catch { /* ignore */ }
    }, 1500);
    return () => clearInterval(id);
  }, [entityType, entityId]);

  return (
    <div className="ob-card">
      <h2>{titre}</h2>
      <p className="ob-sub">{sousTitre}</p>
      <PhotoManager
        key={`${entityType}-${entityId}-${rafraichir}`}
        entityType={entityType}
        entityId={entityId}
      />
      {DEV && (
        <button
          type="button"
          className="ob-devphoto"
          onClick={ajouterPhotoDeTest}
          disabled={envoiTest}
        >
          {envoiTest ? 'Envoi…' : 'Ajouter une photo de test (dev)'}
        </button>
      )}
      <button className="ob-btn" disabled={!hasPhoto} onClick={onDone}>
        {hasPhoto ? 'Continuer' : 'Ajoutez une photo pour continuer'}
      </button>
    </div>
  );
};

/**
 * Écran final. Il annonçait « votre site est en ligne » avec un lien cliquable
 * — deux affirmations fausses : le wizard n'appelle jamais /publish (la coloc
 * naît `is_published = false`), et il ne collecte qu'une fraction de ce que la
 * page publique affiche (description, quartier, caractéristiques, photos des
 * parties communes sont vides). Le lien annoncé renvoyait un 404, au moment
 * précis où le bailleur décide s'il fait confiance au produit.
 *
 * L'écran dit donc ce qui est vrai — l'ossature existe, l'adresse est réservée —
 * et renvoie là où le travail se termine.
 */
const DoneStep: React.FC<{ coloc: Coloc }> = ({ coloc }) => (
  <div className="ob-card ob-center">
    <h1>Votre colocation est créée.</h1>
    <p>
      L&apos;ossature est en place : la colocation, une première chambre et sa photo.
    </p>
    <p className="ob-sub">
      Votre adresse est réservée :{' '}
      {/* Texte et non lien : la coloc n'est pas publiée, le lien renverrait
          vers une page inexistante. */}
      <strong className="ob-public-reserved">{coloc.slug}.coloc.app</strong>
    </p>
    <div className="ob-todo">
      <p className="ob-todo-title">Votre site n&apos;est pas encore en ligne.</p>
      <p>
        Il manque encore la présentation de la colocation et du quartier, ses
        caractéristiques, et les photos des parties communes. Votre espace vous
        guide pour compléter tout ça, puis publier.
      </p>
    </div>
    {/* Une seule sortie, vers le tableau de bord : c'est lui qui porte la carte
        de publication et la liste « Pour aller plus loin ». Reproduire cette
        liste ici la ferait diverger à la première évolution. */}
    <button
      className="ob-btn"
      onClick={() => { window.location.href = '/dashboard'; }}
    >
      Continuer dans mon espace
    </button>
  </div>
);

export default OnboardingPage;
